import React from "react";
import classNames from "classnames";

/* Config */

/* Components */

/* APIs - Hooks - Utils */
import { Info } from "../info/infoApi";
import { PullRequest } from "./metadataApi";
import { Status } from "../dashboard/dashboardApi";

import "react-grid-layout/css/styles.css";

export interface StatusCardProps {
  status: Status;
}
export const StatusCard = ({ status }: StatusCardProps): JSX.Element => {
  return (
    <MetadataCard
      title="Status"
      headerRight={
        <button
          type="button"
          className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold"
          title="Refresh status"
        >
          Refresh
        </button>
      }
      footer={
        <span className="text-gray-500 italic text-xxs">
          Last Updated: {status.last_updated}
        </span>
      }
    >
      <MetadataCardContent
        items={[
          { label: "ID", value: status.id },
          {
            label: "Status",
            value: (
              <span
                className={classNames(
                  "inline-block px-1 py-0.5 rounded-full text-xxs font-semibold",
                  {
                    "bg-green-100 text-green-800": status.status === "up",
                    "bg-yellow-100 text-yellow-800": status.status === "slow",
                    "bg-red-100 text-red-800": status.status === "down",
                    "bg-gray-100 text-gray-800":
                      status.status !== "up" &&
                      status.status !== "slow" &&
                      status.status !== "down",
                  }
                )}
              >
                {status.status?.toUpperCase()}
              </span>
            ),
          },
          { label: "Monitor", value: status.monitor },
          { label: "Avg. Response", value: `${status.response_time_avg} ms` },
        ]}
      />
    </MetadataCard>
  );
};

export interface InfoCardProps {
  appInfo: Info;
}

export const InfoCard = ({ appInfo }: InfoCardProps): JSX.Element => {
  return (
    <MetadataCard
      title="App Info"
      footer={
        appInfo.user_manual_url ? (
          <a
            href={appInfo.user_manual_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            User Manual
          </a>
        ) : undefined
      }
    >
      <MetadataCardContent
        items={[
          { label: "Name", value: appInfo.name },
          { label: "Version", value: appInfo.version },
          { label: "Description", value: appInfo.description },
          {
            label: "Authors",
            value: Object.values(appInfo.authors).join(", "),
          },
        ]}
      />
    </MetadataCard>
  );
};

interface PullRequestCardProps {
  isDependabot?: boolean;
  pullRequests: PullRequest[];
}

export const PullRequestCard: React.FC<PullRequestCardProps> = ({
  isDependabot = false,
  pullRequests,
}) => {
  const header = isDependabot ? "Dependabot PRs" : "Development PRs";

  if (!pullRequests || pullRequests.length === 0) {
    return (
      <div className="w-full bg-white shadow rounded-lg overflow-hidden text-xs flex flex-col h-full p-4">
        <h3 className="text-base font-bold text-gray-900 mb-2">{header}</h3>
        <div className="text-gray-500 italic">No open pull requests.</div>
      </div>
    );
  }

  return (
    <MetadataCard title={header}>
      <MetadataCardContent
        columns={1}
        items={[
          {
            label: "Pull Requests",
            value: <PullRequestTable pullRequests={pullRequests} />,
          },
        ]}
      />
    </MetadataCard>
  );
};

interface MetadataCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  headerRight?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

export const MetadataCard: React.FC<MetadataCardProps> = ({
  title,
  headerRight,
  footer,
  children,
  className = "",
  ...rest
}) => (
  <div
    className={`w-full bg-white shadow rounded-lg overflow-hidden text-xs flex flex-col h-full ${className}`}
    {...rest}
  >
    <div className="px-2 py-2 flex items-center justify-between">
      <h3 className="text-base font-bold text-gray-900">{title}</h3>
      {headerRight}
    </div>
    <div className="border-t border-gray-200 flex-1 overflow-auto">
      {children}
    </div>
    {footer && (
      <div className="border-t border-gray-100 px-2 py-1 bg-gray-50">
        {footer}
      </div>
    )}
  </div>
);

interface MetadataCardContentItem {
  label: string;
  value: React.ReactNode;
}

interface MetadataCardContentProps {
  items: MetadataCardContentItem[];
  columns?: number;
}

export const MetadataCardContent: React.FC<MetadataCardContentProps> = ({
  items,
  columns = 2,
}) => {
  return (
    <dl
      className={classNames("grid gap-x-4 gap-y-2 px-2 py-2", {
        [`grid-cols-${columns}`]: columns > 1,
      })}
    >
      {items.map((item, idx) => (
        <div key={idx} className="flex flex-col group">
          <dt className="font-semibold text-gray-700 uppercase text-xxs mb-0.5">
            {item.label}
          </dt>
          <dd className="text-gray-900 bg-gray-50 rounded px-2 py-1 border border-gray-100 transition-colors group-hover:bg-blue-50 group-hover:border-blue-200">
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
};

interface PullRequestTableProps {
  pullRequests: PullRequest[];
}

export const PullRequestTable: React.FC<PullRequestTableProps> = ({
  pullRequests,
}) => {
  if (!pullRequests || pullRequests.length === 0) {
    return (
      <div className="text-gray-500 italic p-4">No pull requests found.</div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-2 py-1 text-left font-semibold text-gray-700">
              #
            </th>
            <th className="px-2 py-1 text-left font-semibold text-gray-700">
              Title
            </th>
            <th className="px-2 py-1 text-left font-semibold text-gray-700">
              State
            </th>
            <th className="px-2 py-1 text-left font-semibold text-gray-700">
              Draft
            </th>
            <th className="px-2 py-1 text-left font-semibold text-gray-700">
              Author
            </th>
            <th className="px-2 py-1 text-left font-semibold text-gray-700">
              Created
            </th>
            <th className="px-2 py-1 text-left font-semibold text-gray-700">
              From
            </th>
            <th className="px-2 py-1 text-left font-semibold text-gray-700">
              To
            </th>
            <th className="px-2 py-1 text-left font-semibold text-gray-700">
              Merged At
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {pullRequests.map((pr, idx) => (
            <tr
              key={pr.number}
              className={`${
                idx % 2 === 0 ? "bg-white" : "bg-gray-50"
              } hover:bg-blue-50 transition-colors`}
            >
              <td className="px-2 py-1 max-w-[60px] truncate">
                <a
                  href={pr.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-blue-600 hover:underline"
                  title={`#${pr.number}`}
                >
                  #{pr.number}
                </a>
              </td>
              <td className="px-2 py-1 max-w-[180px] truncate" title={pr.title}>
                {pr.title}
              </td>
              <td className="px-2 py-1">
                <span
                  className={classNames(
                    "inline-block px-1 py-0.5 rounded-full text-xxs font-semibold",
                    {
                      "bg-green-100 text-green-800": pr.state === "open",
                      "bg-red-100 text-red-800": pr.state === "closed",
                      "bg-gray-100 text-gray-800":
                        pr.state !== "open" && pr.state !== "closed",
                    }
                  )}
                >
                  {pr.state}
                </span>
              </td>
              <td className="px-2 py-1">
                {pr.draft ? (
                  <span className="inline-block px-1 py-0.5 rounded-full bg-yellow-100 text-yellow-800 text-xxs font-semibold">
                    Yes
                  </span>
                ) : (
                  <span className="inline-block px-1 py-0.5 rounded-full bg-green-50 text-green-700 text-xxs font-semibold">
                    No
                  </span>
                )}
              </td>
              <td
                className="px-2 py-1 text-gray-500 max-w-[80px] truncate"
                title={pr.author}
              >
                {pr.author}
              </td>
              <td className="px-2 py-1 text-gray-400">
                {new Date(pr.created_at).toLocaleDateString()}
              </td>
              <td
                className="px-2 py-1 max-w-[80px] truncate"
                title={pr.head_ref}
              >
                {pr.head_ref}
              </td>
              <td
                className="px-2 py-1 max-w-[80px] truncate"
                title={pr.base_ref}
              >
                {pr.base_ref}
              </td>
              <td className="px-2 py-1">
                {pr.merged_at ? (
                  new Date(pr.merged_at).toLocaleDateString()
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
